import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, useApolloClient } from '@apollo/client';
import { AppContext } from '../../../../providers/AppProvider';
import {
  PostItemFragment,
  GetPostListAscendingQuery,
  GetPostListAscendingQueryVariables,
  GetPostListDescendingQuery,
  GetPostListDescendingQueryVariables,
  GetPostQuery,
  GetPostScheduleListQuery,
  GetPostScheduleWeekQuery,
  QueueContext,
  QueueFilterInput,
  QueueFilterPostStatus,
  QueueFilterPostTiming,
  QueueSortOrder,
  useGetPostListAscendingLazyQuery,
  useGetPostListDescendingLazyQuery,
  useGetPostQueueCountLazyQuery
} from '../../../../models/graphql';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { clearPostCache, defaultQueueInput } from '../PostSchedule.apollo';
import { PostScheduleListController } from './PostScheduleList.controller';
import { PostScheduleViewProps } from '../PostSchedule.view';
import { showToast } from '../../../../helpers/showToast';
import { QueueCountContext } from '../../../../providers/QueueCountProvider';
import { PostScheduleContext } from '../../../../providers/PostScheduleProvider';
import useFetchQueueWithPolling, { UseQueryPollingProps } from '../../../../hooks/useFetchQueueWithPolling';
import { isEqual } from 'lodash';
import { useDidMount } from '../../../../hooks/useDidMount';
import { TopNavContext } from '../../../../providers/TopNavProvider';
import { PostScheduleHelpers } from '../postScheduleHelpers';
import { latestFilterKey } from '../../../../apollo/postScheduleMergePolicy';

export type PostScheduleListApolloProps = PostScheduleViewProps & {
  // ...
};

export type ReFetchFlags = {
  triggerDeleteReFetch?: boolean;
  triggerReShuffleFetch?: boolean;
  triggerPostReFetch?: string;
};

export type LimitsReachedType = {
  upper: boolean;
  lower: boolean;
};

export type postScheduleListOptionsType = (GetPostListDescendingQueryVariables | GetPostListAscendingQueryVariables) & {
  filterKey: string;
};

export const isGetPostScheduleListQuery = (
  data: GetPostQuery | GetPostScheduleListQuery | GetPostScheduleWeekQuery | undefined
): data is GetPostScheduleListQuery => {
  return (
    data !== undefined && 'workspace' in data && 'queue' in data.workspace && 'cursorPagination' in data.workspace.queue
  );
};

export const PostScheduleListApollo = (props: PostScheduleListApolloProps): JSX.Element => {
  const { cache } = useApolloClient();
  const numberOfPostsToFetch = 25;
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [limitsReached, setLimitsReached] = useState<LimitsReachedType>({ upper: false, lower: false });
  const [pollingTimedOut, setPollingTimedOut] = useState<boolean>(false);

  const { selectedWorkspaceId } = useContext(AppContext);
  const {
    loadingInitial,
    setLoadingInitial,
    isMovingPost,
    setPostsCount,
    triggerPostReFetch,
    setTriggerPostReFetch,
    triggerReShuffleFetch,
    setTriggerReShuffleFetch,
    triggerDeleteReFetch,
    setTriggerDeleteReFetch,
    visiblePostIdsRef
  } = useContext(PostScheduleContext);

  const { setQueueCounts } = useContext(QueueCountContext);
  const { reFetch, setRefetch } = useContext(PostScheduleContext);
  const { dispatch: navContextDispatch } = useContext(TopNavContext);

  const descendingPolling = useFetchQueueWithPolling();
  const ascendingPolling = useFetchQueueWithPolling();

  const [corePostItems, setCorePostItems] = useState<PostItemFragment[]>([]);
  const [getPostScheduleListAscending, postListAscendingResults] = useGetPostListAscendingLazyQuery({
    fetchPolicy: 'network-only'
  });

  // Descending is only used for scroll up || upper bounds queries
  const [getPostScheduleListDescending, postListDescendingResults] = useGetPostListDescendingLazyQuery({
    fetchPolicy: 'network-only'
  });

  const [getPostQueueCount] = useGetPostQueueCountLazyQuery();

  const { queueFilters } = props;
  const queueFiltersInput = useMemo(
    (): QueueFilterInput => ({
      status: queueFilters.status,
      searchTerm: queueFilters.searchTerm,
      socialProfileId: queueFilters.socialProfileId,
      categoryId: queueFilters.categoryId,
      timing: queueFilters.timing,
      evergreen: queueFilters.evergreen,
      source: queueFilters.source,
      content: queueFilters.content
    }),
    [queueFilters]
  );

  // We use Ascending as that is our default order and is always called initially
  const {
    fetchMore: postListAscendingResultsFetchMore,
    data: postsAscending,
    variables: postListAscendingResultsVariables
  } = postListAscendingResults;

  const {
    fetchMore: postListDescendingResultsFetchMore,
    data: postsDescending,
    variables: postListDescendingResultsVariables
  } = postListDescendingResults; // there's a typo in 'postListDescendingResults', it should probably be 'postListDescendingResults'

  const getPostScheduleQueueCounts = useCallback(
    (queueFilters?: QueueFilterInput) => {
      // Allow passing of queueFilters to over-ride existing filters
      const filters = queueFilters || queueFiltersInput;
      getPostQueueCount({
        variables: {
          workspaceId: selectedWorkspaceId as string,
          queueFilters: filters,
          queueContext: QueueContext.SCHEDULE
        },
        fetchPolicy: 'network-only'
      })
        .then(results => {
          if (results.data?.workspace.queueCounts) {
            setQueueCounts(results.data.workspace.queueCounts);
          }
        })
        .catch((error: ApolloError) => {
          showToast({
            severity: 'error',
            summary: 'Retrieving Queue Counts',
            detail: remapApolloErrors(error)
          });
        });
    },
    [getPostQueueCount, queueFiltersInput, selectedWorkspaceId, setQueueCounts]
  );

  type FetchDetails = {
    currentPosts: typeof postsAscending;
    fetchMoreFunction: typeof postListAscendingResultsFetchMore;
    variables: typeof postListAscendingResultsVariables;
    fetchOrder: QueueSortOrder;
  };

  const determineFetchDetails = useCallback(
    (scrollPosition: 'TOP' | 'BOTTOM', sortOrder: QueueSortOrder): FetchDetails => {
      if (scrollPosition === 'BOTTOM') {
        if (sortOrder === QueueSortOrder.ASCENDING) {
          return {
            currentPosts: postsAscending,
            fetchMoreFunction: postListAscendingResultsFetchMore,
            variables: postListAscendingResultsVariables,
            fetchOrder: QueueSortOrder.ASCENDING
          };
        } else {
          return {
            currentPosts: postsDescending,
            fetchMoreFunction: postListDescendingResultsFetchMore,
            variables: postListDescendingResultsVariables,
            fetchOrder: QueueSortOrder.DESCENDING
          };
        }
      } else {
        if (sortOrder === QueueSortOrder.ASCENDING) {
          return {
            currentPosts: postsDescending,
            fetchMoreFunction: postListDescendingResultsFetchMore,
            variables: postListDescendingResultsVariables,
            fetchOrder: QueueSortOrder.DESCENDING
          };
        } else {
          return {
            currentPosts: postsAscending,
            fetchMoreFunction: postListAscendingResultsFetchMore,
            variables: postListAscendingResultsVariables,
            fetchOrder: QueueSortOrder.ASCENDING
          };
        }
      }
    },
    [
      postListAscendingResultsFetchMore,
      postListAscendingResultsVariables,
      postListDescendingResultsFetchMore,
      postListDescendingResultsVariables,
      postsAscending,
      postsDescending
    ]
  );

  const fetchPostsWithPolling = useCallback(
    async (options: postScheduleListOptionsType, sortOrder: QueueSortOrder, scrollPosition: 'TOP' | 'BOTTOM') => {
      setPollingTimedOut(false);

      let useQueryPollingProps:
        | UseQueryPollingProps<GetPostListDescendingQuery, GetPostListDescendingQueryVariables>
        | UseQueryPollingProps<GetPostListAscendingQuery, GetPostListAscendingQueryVariables>;

      if (sortOrder === QueueSortOrder.DESCENDING) {
        useQueryPollingProps = {
          queryFn: getPostScheduleListDescending,
          options
        };
      } else {
        useQueryPollingProps = {
          queryFn: getPostScheduleListAscending,
          options
        };
      }

      const polling = sortOrder === QueueSortOrder.DESCENDING ? descendingPolling : ascendingPolling;

      await polling
        .startQuery(useQueryPollingProps)
        .then(({ data }) => {
          // Make sure that are filterKey is current filter otherwise ignore stale return
          if (isGetPostScheduleListQuery(data) && latestFilterKey.key && options.filterKey === latestFilterKey.key) {
            const newPosts = data.workspace.queue.cursorPagination?.items || [];

            PostScheduleHelpers.updatePostsAndLimits({
              posts: newPosts,
              scrollPosition,
              fetchOrder: sortOrder,
              numberOfPostsToFetch,
              setCorePostItems,
              setLimitsReached
            });
          }
        })
        .catch(error => {
          if (error && error.message === 'POST_CALCULATION_TIMEOUT') {
            setPollingTimedOut(true);
          }
        });
    },
    [ascendingPolling, descendingPolling, getPostScheduleListAscending, getPostScheduleListDescending]
  );

  const fetchInitialPostItems = useCallback(
    (queueFilters?: QueueFilterInput) => {
      // Allow passing of queueFilters to over-ride existing filters
      const filters = queueFilters || queueFiltersInput;
      setLoadingInitial(true);
      setCorePostItems([]);
      setLimitsReached({ lower: false, upper: true });

      if (visiblePostIdsRef) {
        visiblePostIdsRef.current = [];
      }

      if (selectedWorkspaceId) {
        clearPostCache(selectedWorkspaceId);
      }

      const sortType = PostScheduleHelpers.mapStatusToSortType(filters.status || undefined);
      const sortOrder = PostScheduleHelpers.mapStatusToSortOrder(sortType);
      const filterKey = PostScheduleHelpers.generateQueueFilterHash(filters);

      const options: postScheduleListOptionsType = {
        filterKey,
        workspaceId: selectedWorkspaceId as string,
        input: {
          ...defaultQueueInput,
          filters: {
            ...filters
          },
          ...{
            pagination: {
              cursorPagination: {
                count: numberOfPostsToFetch,
                sortType,
                sortOrder
              }
            },
            context: QueueContext.SCHEDULE
          }
        }
      };

      latestFilterKey.key = filterKey;

      fetchPostsWithPolling(options, sortOrder, 'BOTTOM').finally(() => {
        setLoadingInitial(false);
      });
    },
    [fetchPostsWithPolling, queueFiltersInput, selectedWorkspaceId, setLoadingInitial, visiblePostIdsRef]
  );

  const fetchUpperAndLowerBounds = useCallback(
    (post: PostItemFragment, filters: QueueFilterInput) => {
      const cursor = PostScheduleHelpers.getCursorFromPostId(post.id);
      const sortType = PostScheduleHelpers.mapStatusToSortType(filters.status || undefined);
      const filterKey = PostScheduleHelpers.generateQueueFilterHash(filters);

      if (selectedWorkspaceId) {
        clearPostCache(selectedWorkspaceId);
      }

      const fetchOptions = (sortOrder: QueueSortOrder): postScheduleListOptionsType => ({
        filterKey,
        workspaceId: selectedWorkspaceId as string,
        input: {
          ...defaultQueueInput,
          filters,
          pagination: {
            cursorPagination: {
              cursor,
              count: numberOfPostsToFetch,
              sortType,
              sortOrder
            }
          }
        }
      });

      setLoadingInitial(true);

      latestFilterKey.key = filterKey;

      Promise.all([
        fetchPostsWithPolling(fetchOptions(QueueSortOrder.ASCENDING), QueueSortOrder.ASCENDING, 'BOTTOM'),
        fetchPostsWithPolling(fetchOptions(QueueSortOrder.DESCENDING), QueueSortOrder.DESCENDING, 'TOP')
      ])
        .catch(error => {
          if (getApolloErrorMessage(error) === 'PAGINATION_CURSOR_INVALID') {
            fetchInitialPostItems(queueFiltersInput);
          }
        })
        .finally(() => {
          setLoadingInitial(false);
        });
    },
    [fetchInitialPostItems, fetchPostsWithPolling, queueFiltersInput, selectedWorkspaceId, setLoadingInitial]
  );

  const fetchPostsAfterPostMove = useCallback(
    (post: PostItemFragment, filters: QueueFilterInput) => {
      corePostItems.forEach(p => {
        cache.evict({ id: cache.identify({ id: p.id, __typename: 'Post' }) });
      });

      cache.gc();

      setCorePostItems([]);

      fetchUpperAndLowerBounds(post, filters);
    },
    [cache, corePostItems, fetchUpperAndLowerBounds]
  );

  const fetchMorePostItems = useCallback(
    (scrollPosition: 'TOP' | 'BOTTOM') => {
      if (!selectedWorkspaceId) return;
      const status = queueFiltersInput.status || QueueFilterPostStatus.SCHEDULED;

      const sortOrder = PostScheduleHelpers.determineSortOrder(status);
      const { currentPosts, fetchMoreFunction, variables, fetchOrder } = determineFetchDetails(
        scrollPosition,
        sortOrder
      );

      if (!currentPosts || !variables) return;

      setLoadingMore(true);

      const cursor = PostScheduleHelpers.determineCursorFromScrollPosition(corePostItems, scrollPosition);
      const filterKey = PostScheduleHelpers.generateQueueFilterHash(variables.input.filters);

      latestFilterKey.key = filterKey;
      fetchMoreFunction({
        variables: {
          ...variables,
          filterKey,
          input: {
            ...variables.input,
            filters: variables.input.filters,
            pagination: {
              cursorPagination: {
                ...variables.input.pagination.cursorPagination,
                cursor,
                sortOrder: fetchOrder
              }
            }
          }
        }
      })
        .then(results => {
          // Make sure that are filterKey is current filter otherwise ignore stale return
          if (latestFilterKey.key && filterKey === latestFilterKey.key) {
            const newPosts = results.data?.workspace.queue.cursorPagination?.items || [];

            PostScheduleHelpers.updatePostsAndLimits({
              posts: newPosts,
              scrollPosition,
              fetchOrder,
              numberOfPostsToFetch,
              setCorePostItems,
              setLimitsReached
            });
          }
        })
        .finally(() => {
          setLoadingMore(false);
        });
    },
    [selectedWorkspaceId, queueFiltersInput.status, determineFetchDetails, corePostItems]
  );

  const fetchSubQueuePosts = useCallback(
    (post: PostItemFragment, filters: QueueFilterInput) => {
      corePostItems.forEach(p => {
        if (p.id !== post.id) {
          cache.evict({ id: cache.identify({ id: p.id, __typename: 'Post' }) });
        }
      });
      cache.gc();

      setCorePostItems([post]);

      filters = {
        ...filters,
        ...{
          socialProfileId: post.socialProfile?.id,
          categoryId: post.category.id,
          timing: QueueFilterPostTiming.QUEUED
        }
      };

      Promise.all([fetchUpperAndLowerBounds(post, filters), getPostScheduleQueueCounts(filters)]).finally();
    },
    [cache, corePostItems, fetchUpperAndLowerBounds, getPostScheduleQueueCounts]
  );

  const queueFiltersInputRef = useRef(queueFiltersInput);

  const handleFetch = useCallback(
    async (shouldResetReFetchFlags: ReFetchFlags = {}) => {
      try {
        await Promise.all([fetchInitialPostItems(queueFiltersInput), getPostScheduleQueueCounts(queueFiltersInput)]);

        // Reset specific re-fetch flags if defined
        if (shouldResetReFetchFlags.triggerDeleteReFetch !== undefined) {
          setTriggerDeleteReFetch(shouldResetReFetchFlags.triggerDeleteReFetch);
        }

        if (shouldResetReFetchFlags.triggerReShuffleFetch !== undefined) {
          setTriggerReShuffleFetch(shouldResetReFetchFlags.triggerReShuffleFetch);
        }

        if (shouldResetReFetchFlags.triggerPostReFetch !== undefined) {
          setTriggerPostReFetch(shouldResetReFetchFlags.triggerPostReFetch);
        }
      } catch (error) {
        showToast({
          severity: 'error',
          summary: 'Retrieving Recalculated Queue',
          detail: remapApolloErrors(error as ApolloError) // Error is of unknown so we have to cast
        });
      }
    },
    [
      fetchInitialPostItems,
      getPostScheduleQueueCounts,
      queueFiltersInput,
      setTriggerDeleteReFetch,
      setTriggerPostReFetch,
      setTriggerReShuffleFetch
    ]
  );

  useEffect(() => {
    if (reFetch) {
      if (selectedWorkspaceId) {
        clearPostCache(selectedWorkspaceId);
      }

      handleFetch({ triggerPostReFetch: '' }).finally(() => {
        setRefetch(false);
      });
    }
  }, [handleFetch, reFetch, selectedWorkspaceId, setLoadingInitial, setRefetch]);

  useEffect(() => {
    if (!isMovingPost && !isEqual(queueFiltersInputRef.current, queueFiltersInput)) {
      handleFetch().finally();
    }
    // Always set to keep ref up to date when filters change
    queueFiltersInputRef.current = queueFiltersInput;
  }, [handleFetch, isMovingPost, queueFiltersInput]);

  useEffect(() => {
    if (triggerPostReFetch) {
      handleFetch({ triggerPostReFetch: '' }).finally();
    }
  }, [triggerPostReFetch, handleFetch]);

  useEffect(() => {
    if (triggerDeleteReFetch) {
      handleFetch({ triggerDeleteReFetch: false }).finally();
    }
  }, [triggerDeleteReFetch, handleFetch]);

  useEffect(() => {
    if (triggerReShuffleFetch) {
      handleFetch({ triggerReShuffleFetch: false }).finally();
    }
  }, [triggerReShuffleFetch, handleFetch]);

  useEffect(() => {
    setPostsCount(corePostItems.length);
  }, [corePostItems, setPostsCount]);

  useEffect(() => {
    navContextDispatch({
      type: 'setNewPostCloseCallback',
      payload: status => {
        if (status) {
          fetchInitialPostItems();
          getPostScheduleQueueCounts();
        }
      }
    });

    return () => {
      navContextDispatch({
        type: 'setNewPostCloseCallback',
        payload: undefined
      });
    };
  }, [fetchInitialPostItems, getPostScheduleQueueCounts, navContextDispatch]);

  // If queueFilters change and fetchPosts is true, initiate new queries always
  useEffect(() => {
    if (queueFilters.fetchPosts) {
      Promise.all([fetchInitialPostItems(), getPostScheduleQueueCounts()]).finally();
    }
  }, [fetchInitialPostItems, getPostScheduleQueueCounts, queueFilters]);

  useDidMount(() => {
    Promise.all([fetchInitialPostItems(), getPostScheduleQueueCounts()]).finally();
  });

  const controllerProps: PostScheduleViewProps = {
    ...props,
    ...{
      loadingInitial,
      loadingMore
    }
  };

  return (
    <PostScheduleListController
      pollingTimedOut={pollingTimedOut}
      limitsReached={limitsReached}
      setLimitsReached={setLimitsReached}
      fetchMorePostItems={fetchMorePostItems}
      fetchSubQueuePosts={fetchSubQueuePosts}
      fetchPostsAfterPostMove={fetchPostsAfterPostMove}
      posts={corePostItems}
      queueFiltersInput={queueFiltersInput}
      {...controllerProps}
    />
  );
};
